import { useState, useEffect, useCallback } from 'react';
import type { IdentityDocument } from '../../../../modules/identity/types';
import { getDocumentTypeLabel } from '../../../../modules/identity/helpers';
import { getIdentityViewUrl } from '../../../../services/identityGatewayService';

interface ViewIdentityDocumentModalProps {
  document: IdentityDocument;
  customerName: string;
  onClose: () => void;
}

/**
 * 查看證件彈窗
 *
 * 安全規則：
 * - 不可直接顯示 public URL
 * - 必須呼叫 customer-identity-document-read Edge Function 取得短效 signed URL
 * - 每次查看必須寫入 audit log（由 Edge Function 處理）
 * - 不可在 console log 或 DebugDrawer 顯示 signed URL
 */
export function ViewIdentityDocumentModal({
  document,
  customerName,
  onClose,
}: ViewIdentityDocumentModalProps): JSX.Element {
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(300);

  // 呼叫 gateway 取得 signed URL
  const fetchSignedUrl = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getIdentityViewUrl(
        document.id,
        'admin_view_identity_document',
        300
      );
      setSignedUrl(response.signedUrl);
      setCountdown(300);
    } catch (err: any) {
      setError(err.message || '無法取得證件影像');
    } finally {
      setLoading(false);
    }
  }, [document.id]);

  useEffect(() => {
    fetchSignedUrl();
  }, [fetchSignedUrl]);

  // 倒數計時
  useEffect(() => {
    if (!signedUrl || countdown <= 0) return;
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          setSignedUrl(null);
          setError('signed URL 已過期，請關閉視窗重新查看');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [signedUrl, countdown]);

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
          <div>
            <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
              <i className="ri-shield-keyhole-line text-teal-600"></i>
              查看證件
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {customerName} · {getDocumentTypeLabel(document.document_type)}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {signedUrl && countdown > 0 && (
              <span className="text-xs text-amber-600 font-medium">
                剩餘 {Math.floor(countdown / 60)}:{String(countdown % 60).padStart(2, '0')}
              </span>
            )}
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-lg transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* 安全提示 */}
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start gap-2">
              <i className="ri-shield-flash-line text-red-600 mt-0.5"></i>
              <div className="text-xs text-red-700 space-y-1">
                <p className="font-semibold">安全提醒</p>
                <p>此影像僅供 LensBank 內部核驗使用。請勿截圖、複製或傳播。</p>
                <p>URL 將於 5 分鐘後自動失效。</p>
              </div>
            </div>
          </div>

          {/* 影像顯示區 */}
          <div className="relative bg-gray-100 rounded-lg overflow-hidden min-h-[300px] flex items-center justify-center">
            {loading ? (
              <div className="flex flex-col items-center gap-3 py-12">
                <i className="ri-loader-4-line animate-spin text-2xl text-gray-400"></i>
                <p className="text-sm text-gray-500">正在取得安全影像...</p>
              </div>
            ) : error ? (
              <div className="flex flex-col items-center gap-3 py-12">
                <i className="ri-error-warning-line text-3xl text-red-400"></i>
                <p className="text-sm text-red-600">{error}</p>
                <button
                  onClick={fetchSignedUrl}
                  className="px-4 py-2 bg-gray-100 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  重新取得
                </button>
              </div>
            ) : signedUrl ? (
              <div className="w-full">
                <img
                  src={signedUrl}
                  alt={`${getDocumentTypeLabel(document.document_type)}`}
                  className="w-full object-contain"
                  style={{ maxHeight: '60vh' }}
                />
                {/* 下方資訊 */}
                <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
                  <span className="font-mono">{document.id}</span>
                  <span className="text-amber-600 font-medium">{Math.floor(countdown / 60)}:{String(countdown % 60).padStart(2, '0')} 後失效</span>
                </div>
              </div>
            ) : null}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-gray-50">
          <div className="text-xs text-gray-500">
            證件 ID：<span className="font-mono">{document.id}</span>
          </div>
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-900 text-white font-medium rounded-lg hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
          >
            關閉
          </button>
        </div>
      </div>
    </div>
  );
}